// src/lib/ads-framework/seo.ts

import { siteConfig } from "~/lib/config/site"
import { POLICY_URLS } from "~/lib/legal/policy-urls"
import { posterSocialPathFor } from "~/lib/seo/poster-social-path"
import { normalizeIsoDate } from "~/lib/utils/date"

/**
 * SEO / JSON-LD builders scoped to the ads-framework storefront surfaces.
 *
 * Centralises the framework-graph builders used by the indexable framework
 * pages (/frameworks/ads-framework, /setup, /skills, /frameworks/ads-skills)
 * plus the commerce-specialized Product / SoftwareApplication / Offer builders
 * for the storefront.
 *
 * The generic, site-wide FAQ + breadcrumb builders were RELOCATED in A4 to
 * `lib/metadata/structured-data/` — they are emitted across every surface
 * (resources, comparisons, blog, policy pages), so a feature directory is the
 * wrong home for them. Import `buildFaqJsonLd` / `toFaqEntries` /
 * `buildBreadcrumbJsonLd` from `~/lib/metadata/structured-data` instead.
 *
 * Each builder returns a plain object meant for inclusion in a single
 * page-level `@graph` document. Pages consolidate the entities via
 * `buildSchemaGraph()` and emit one `<script type="application/ld+json">`
 * tag rather than one per entity — keeps the head lean and lets every
 * entity reference the canonical Organization defined in
 * `GlobalStructuredData` by `@id` without redeclaring it.
 */

const SITE_URL = siteConfig.url

/** Canonical `@id` for the Organization defined in `GlobalStructuredData`. */
const ORGANIZATION_ID = `${SITE_URL}/#organization`

/**
 * Escape JSON for safe inclusion inside an inline `<script>` block.
 *
 * Per the OWASP "JSON in HTML" guidance and Next.js's own JSON-LD
 * documentation, the canonical mitigation is to escape `<` (and
 * optionally `>` / `&`) plus the JS line/paragraph separators
 * U+2028 and U+2029 (which terminate a JS string literal in pre-2019
 * parsers and still cause issues in some downstream toolchains).
 *
 * Escaping `<` universally handles every breakout vector in one pass:
 *   - `</script>` (any case: `</SCRIPT`, `</ScRiPt`, etc.)
 *   - `</style>`, `</xmp>`, `</textarea>`, future raw-text elements
 *   - `<!--` (HTML comment injection)
 *   - `<![CDATA[` (XHTML breakout)
 *
 * Previous incremental escapes covered named breakouts only and left
 * the door open to whichever variant a future parser quirk surfaced.
 * This wholesale `<` → `<` rule is what the OWASP guide and
 * Vercel's reference Next.js JSON-LD example both use.
 */
export function safeJsonLdStringify(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029")
}

/**
 * Wrap a list of schema.org entities in a single `@graph` document.
 * Entities should NOT carry their own `@context` — the outer graph's
 * context applies to all of them, which is the spec-compliant pattern
 * and what Google's rich-result extractor expects when entities cross-
 * reference each other via `@id`.
 */
export function buildSchemaGraph(
  entities: ReadonlyArray<Record<string, unknown>>,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@graph": entities.map((entity) => {
      // Defence-in-depth: builders below already omit @context, but if
      // a future caller hands us an entity that carries one, strip it
      // so it can't conflict with the outer graph context.
      if ("@context" in entity) {
        const { ["@context"]: _omit, ...rest } = entity
        return rest
      }
      return entity
    }),
  }
}

export interface WebPageGraphInput {
  /** Page name as it appears in the schema (typically the page title). */
  name: string
  /** Absolute URL — also used as `@id` with the `#webpage` fragment. */
  url: string
  /** Optional URL of the parent collection (e.g. the framework hub). */
  parentUrl?: string
  /** Optional list of child URLs (each becomes a `hasPart` entry). */
  childUrls?: ReadonlyArray<string>
  /** Optional `primaryImageOfPage.url` — defaults to OG image if used. */
  primaryImageUrl?: string
  /** Optional `dateModified` (ISO 8601). */
  dateModified?: string
}

/**
 * Build a WebPage entity that participates in the framework's
 * cross-page graph. `isPartOf` references the framework hub so the
 * four storefront pages read as one logical product surface to AI
 * answer engines and Google's knowledge graph. `hasPart` on the hub
 * surfaces the subpages as discoverable children.
 *
 * The entity uses `#webpage` `@id` convention matching the existing
 * `WithPageStructuredData` pattern; pages are free to link to it from
 * other entities (e.g. `mainEntity` on a homepage WebPage references
 * the hero VideoObject's `@id`).
 */
export function buildWebPageJsonLd(input: WebPageGraphInput): Record<string, unknown> {
  const entity: Record<string, unknown> = {
    "@type": "WebPage",
    "@id": `${input.url}#webpage`,
    url: input.url,
    name: input.name,
    isPartOf: { "@id": `${SITE_URL}/#website` },
    inLanguage: "en-US",
  }
  if (input.parentUrl) {
    // Subpages reference the hub's WebPage entity by id so a
    // graph traversal can group the four framework URLs as one
    // product surface. Both directions explicit (parent's hasPart
    // PLUS child's isPartOf) — overpriced for byte count, but
    // crawlers consume directed graphs and the redundancy makes
    // traversal robust to extractor-specific quirks.
    entity.isPartOf = [
      { "@id": `${SITE_URL}/#website` },
      { "@id": `${input.parentUrl}#webpage` },
    ]
  }
  if (input.childUrls && input.childUrls.length > 0) {
    entity.hasPart = input.childUrls.map((u) => ({ "@id": `${u}#webpage` }))
  }
  if (input.primaryImageUrl) {
    entity.primaryImageOfPage = {
      "@type": "ImageObject",
      url: input.primaryImageUrl,
    }
  }
  if (input.dateModified) {
    entity.dateModified = input.dateModified
  }
  return entity
}

export interface VideoEntry {
  /** Short label shown on the demo tile (e.g. "Sale promo"). */
  label: string
  /** Format id from the demo manifest (e.g. "social-square"). */
  format: string
  /** Width in pixels. */
  width: number
  /** Height in pixels. */
  height: number
  /** Duration in seconds. */
  durationSeconds: number
  /** Path to the MP4 (relative to /demos/, e.g. "sale-promo__social-square.mp4"). */
  contentPath: string
  /** Optional poster image path; bare filename or `posters/<file>`. */
  posterPath?: string
  /**
   * Per-format pitch / content-equivalent text. When present it's
   * used as the VideoObject `description`, which AI-search engines and
   * Google's transcript-derived ranking match against the spoken
   * content. Should mirror the visible per-tile caption rendered on
   * the page.
   */
  pitch?: string
}

/**
 * Build a schema.org VideoObject for a rendered demo MP4. Google
 * surfaces VideoObject in video carousels + Knowledge Panels when the
 * page embeds the video inline. ISO-8601 duration follows the
 * `PT<seconds>S` short form Google accepts.
 *
 * `uploadDate` should be the timestamp the demo MP4 was rendered (pass
 * `manifest.generatedAt` from the loaded demos manifest).
 *
 * `embedUrl` is intentionally omitted — Google's spec defines it as the
 * URL of an embeddable PLAYER, not the direct MP4. Setting it equal to
 * `contentUrl` provides no signal and trips Rich Results Test
 * redundancy warnings. `contentUrl` alone satisfies the required-field
 * check for a self-hosted MP4 with no separate player surface.
 *
 * `publisher` references the canonical Organization defined in
 * `GlobalStructuredData` by `@id` so the page doesn't ship a duplicate
 * Organization entity per VideoObject (the gallery emits one
 * VideoObject per rendered demo — up to N×M — so the dedup matters).
 */
/**
 * Build the description text for a rendered demo video. Shared by the
 * VideoObject JSON-LD (`buildVideoObjectJsonLd` below) and the
 * `<video:description>` field in `core.ts`'s sitemap provider.
 *
 * The manifest may emit an empty-string `pitch` for an under-curated
 * entry (a concept added before its per-format pitch was authored).
 * Both consumers MUST fall back to the descriptive template on both
 * undefined AND empty-string:
 *   - Google's video-sitemap spec rejects entries with an empty
 *     `<video:description>` (`Required field is missing`).
 *   - VideoObject `description` is also a Google-required field and
 *     omitting it disqualifies the entity from video rich results.
 *
 * `||` (not `??`) is the correct fallback operator: nullish coalescing
 * passes `""` through, which is exactly the bug this helper guards
 * against. A dedicated test in `seo.test.ts` pins the empty-pitch
 * behavior.
 */
export function videoDescriptionFor(video: VideoEntry): string {
  const fallback = `Rendered ad demo: ${video.label} concept at ${video.format} format (${video.width}×${video.height}), produced by the Adsights Ads Framework Remotion engine.`
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  return video.pitch || fallback
}

export function buildVideoObjectJsonLd(
  video: VideoEntry,
  uploadDate: string,
  siteUrl: string = SITE_URL,
): Record<string, unknown> {
  const contentUrl = `${siteUrl}/demos/${video.contentPath}`
  // Manifest stores posters under `posters/<file>` on disk, but the
  // public `/demos/<file>` URL is flat — the route handler resolves
  // image extensions into the posters/ subdir itself. Strip the
  // directory prefix loudly with a `replace` so a future
  // `subset/posters/foo.webp` manifest entry fails fast instead of
  // silently 404ing.
  const thumbnailFilename = video.posterPath?.replace(/^posters\//, "")
  // JPEG social sibling — gallery VideoObject thumbnails are crawler-visible
  // (same WebP-drop class as #660). On-page `<video poster>` stays on WebP.
  const thumbnailUrl = thumbnailFilename
    ? `${siteUrl}${posterSocialPathFor(`/demos/${thumbnailFilename}`)}`
    : `${siteUrl}${siteConfig.shareCardPath}`
  const pitchText = videoDescriptionFor(video)
  return {
    "@type": "VideoObject",
    name: `${video.label} — ${video.format} (${video.width}×${video.height})`,
    description: pitchText,
    // `transcript` is a schema.org-defined VideoObject property.
    // 3PlayMedia's 2026 research finds videos with transcripts are
    // ~16% more likely to appear in featured snippets, and AI-search
    // engines (Perplexity, ChatGPT Search, Claude-SearchBot) match
    // against transcript text for citation grounding. These muted-
    // autoplay demos have no spoken content, so the per-format pitch
    // IS the content-equivalent — mirroring it here gives AI engines
    // a third channel for the same data alongside `description` and
    // the visible tile caption.
    transcript: pitchText,
    thumbnailUrl: [thumbnailUrl],
    uploadDate: normalizeIsoDate(uploadDate),
    duration: `PT${Math.max(1, Math.round(video.durationSeconds))}S`,
    contentUrl,
    width: video.width,
    height: video.height,
    isFamilyFriendly: true,
    inLanguage: "en-US",
    publisher: { "@id": ORGANIZATION_ID },
  }
}

export interface SchemaReview {
  /** Author full name (rendered next to the star rating). */
  authorName: string
  /** Optional job title for the author. */
  authorTitle?: string
  /** 1–5 integer star rating. */
  ratingValue: number
  /** Quote body. Should mirror what's visible on the page. */
  reviewBody: string
  /** ISO 8601 publication date (`YYYY-MM-DD` is fine). */
  datePublished: string
}

/**
 * EU-27 ISO-3166-1 alpha-2 codes — the actual member states we serve
 * via Stripe. Maintained as a named export so it stays curated
 * (additions/removals are easy to grep) rather than expanded inline as
 * a 27-element literal at every call site.
 */
export const EU_MEMBER_STATES = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR",
  "DE", "GR", "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL",
  "PL", "PT", "RO", "SK", "SI", "ES", "SE",
] as const

/**
 * Default applicable countries for digital-product offers. ISO-3166-1
 * alpha-2 codes ONLY. The string "EU" looks tempting but is a reserved
 * regional code, not a country — Google's Merchant rich-result parser
 * silently drops it (failing the "shipping coverage matches" check
 * this constant is meant to satisfy), and Bing's structured-data
 * extractor flags the entry. Enumerate member states explicitly.
 */
export const DEFAULT_DIGITAL_OFFER_COUNTRIES = [
  "US", "CA", "GB", "AU", ...EU_MEMBER_STATES,
] as const

const ZERO_DAY_SERVICE_PERIOD = {
  "@type": "ServicePeriod",
  duration: {
    "@type": "QuantitativeValue",
    minValue: 0,
    maxValue: 0,
    unitCode: "DAY",
  },
} as const

/**
 * Build the Organization-level merchant return policy for the
 * framework storefront. Google now supports standard return policies
 * nested under an Organization / OnlineStore entity; product-level
 * Offer policies below remain the per-SKU overrides.
 */
export function buildDigitalStoreReturnPolicy(): Record<string, unknown> {
  return {
    ...buildMerchantReturnPolicy({
      days: 30,
      applicableCountry: DEFAULT_DIGITAL_OFFER_COUNTRIES,
    }),
    name: "AdSights framework storefront return policy",
    refundType: "https://schema.org/FullRefund",
    itemCondition: [
      "https://schema.org/NewCondition",
      "https://schema.org/DamagedCondition",
    ],
    customerRemorseReturnFees: "https://schema.org/FreeReturn",
    itemDefectReturnFees: "https://schema.org/FreeReturn",
  }
}

/**
 * Build the Organization-level shipping service for downloadable
 * framework products. The site sells digital software only: delivery is
 * electronic, immediate, and free across the same countries the
 * storefront serves. Offer-level `shippingDetails` stays in place for
 * Product/MerchantListing eligibility; this OnlineStore-level service
 * feeds merchant knowledge-panel / brand-profile policy surfaces.
 */
export function buildDigitalStoreShippingService(
  priceCurrency = "USD",
): Record<string, unknown> {
  return {
    "@type": "ShippingService",
    "@id": `${POLICY_URLS.shipping}#digital-delivery`,
    name: "Free immediate digital delivery",
    description:
      "AdSights framework products are delivered electronically through the account portal, private npm registry access, and/or self-contained zip downloads. Shipping is always free and available immediately after successful payment.",
    fulfillmentType: "https://schema.org/FulfillmentTypeDelivery",
    handlingTime: ZERO_DAY_SERVICE_PERIOD,
    shippingConditions: {
      "@type": "ShippingConditions",
      shippingDestination: DEFAULT_DIGITAL_OFFER_COUNTRIES.map((country) => ({
        "@type": "DefinedRegion",
        addressCountry: country,
      })),
      shippingRate: {
        "@type": "MonetaryAmount",
        value: 0,
        currency: priceCurrency,
      },
      transitTime: ZERO_DAY_SERVICE_PERIOD,
    },
  }
}

export interface ShippingDetailsInput {
  /** ISO country codes the offer ships to. */
  applicableCountries?: ReadonlyArray<string>
  /** Override the default $0 shipping rate (rare — keep for digital). */
  shippingRate?: { value: number; currency: string }
}

export interface ReturnPolicyInput {
  /**
   * Return window in days (e.g. 30). Ignored when
   * `category === "MerchantReturnNotPermitted"` — Google's parser
   * treats `merchantReturnDays` as exclusive to finite/unlimited
   * windows and emitting it alongside `NotPermitted` makes the
   * structured data internally contradictory.
   */
  days: number
  /**
   * ISO-3166-1 alpha-2 country code, or an array of them when the
   * policy spans multiple destinations. Google's MerchantReturnPolicy
   * spec accepts either a single string or a JSON array here; we
   * accept both so callers whose `shippingDetails` covers multiple
   * countries can mirror the same set on the return policy without
   * having to emit one MerchantReturnPolicy block per country.
   */
  applicableCountry: string | ReadonlyArray<string>
  /**
   * Return-policy category override. Google's MerchantReturnPolicy
   * docs (https://developers.google.com/search/docs/appearance/structured-data/return-policy)
   * support only these three values. Schema.org also defines
   * `MerchantReturnUnspecified`, but Google's parser doesn't accept
   * it for return-policy markup — leaving an Offer with that category
   * INVALID in Search Console, which defeats the purpose of emitting
   * the field at all. We intentionally exclude it from the literal
   * union so the type system prevents the regression.
   *   - `MerchantReturnFiniteReturnWindow` (default — 30/60/90-day windows)
   *   - `MerchantReturnUnlimitedWindow` (unlimited returns)
   *   - `MerchantReturnNotPermitted` (no guaranteed return window)
   */
  category?:
    | "MerchantReturnFiniteReturnWindow"
    | "MerchantReturnUnlimitedWindow"
    | "MerchantReturnNotPermitted"
  /**
   * Canonical URL to the human-readable return policy page. Defaults to
   * `/returns` — required for Google Merchant Center URL verification.
   */
  returnPolicyUrl?: string
}

export interface SoftwareApplicationInput {
  /** Product name as it should appear in SERPs. */
  name: string
  /** Long-form description (1–2 sentences). */
  description: string
  /** Absolute URL for the product's landing page. */
  url: string
  /** Per-unit price in major currency units (e.g. "199.00"). */
  price: string
  /** ISO 4217 currency code (e.g. "USD"). */
  priceCurrency: string
  /** Bulleted feature list shown in SoftwareApplication rich-results. */
  featureList: ReadonlyArray<string>
  /**
   * Optional review array. When present, `aggregateRating` is computed
   * automatically (mean ratingValue, count = reviews.length) so the
   * software entity becomes eligible for star-rating rich results in
   * Google + Bing. Reviews MUST be grounded in visible page content
   * to survive crawl-time verification.
   */
  reviews?: ReadonlyArray<SchemaReview>
  /**
   * Optional semantic-version string (e.g. "0.1.0"). Lets crawlers
   * tie a citation to a specific release of the SKU — useful for AI
   * answer engines that summarize "what changed in vX.Y" queries.
   */
  softwareVersion?: string
  /** Direct download URL (e.g. zip distribution download endpoint). */
  downloadUrl?: string
  /** MIME type of the downloadable artifact (e.g. `application/zip`). */
  fileFormat?: string
  /**
   * Operating systems / runtime requirements beyond the entity's
   * coarse `operatingSystem` value. Free-text per schema.org.
   */
  softwareRequirements?: string
  /**
   * Optional `potentialAction` for the offer — a BuyAction with the
   * machine-readable checkout URL. AI agents (ChatGPT Atlas, Claude
   * agentic mode) follow this for one-hop purchase flows; Princeton's
   * GEO study + AgentReadyHQ measure a ~3.2× citation lift for
   * SKUs that publish a BuyAction over Product+Offer alone.
   */
  buyActionTarget?: string
  /**
   * Return-policy block emitted as `hasMerchantReturnPolicy` on the
   * Offer. Required for Google Merchant rich-result eligibility (Q1
   * 2026 tightening — Search Console flags missing returns under
   * "Top non-critical issues" → "Missing field hasMerchantReturnPolicy").
   * Defaults to a 30-day finite-window policy when omitted so the
   * field is always present (digital SKUs without a stated policy
   * still need the structural signal).
   */
  returnPolicy?: ReturnPolicyInput
  /**
   * Shipping-details block emitted as `shippingDetails` on the Offer.
   * Same Google Merchant 2026 requirement as `hasMerchantReturnPolicy`
   * — Search Console flags missing shipping under "Top non-critical
   * issues" → "Missing field shippingDetails". For digital products
   * the canonical pattern is $0 shipping + 0-day handling/transit
   * (the product is downloadable, but Google still needs the field
   * to grade the offer). Defaults to that pattern when omitted.
   */
  shippingDetails?: ShippingDetailsInput
  /**
   * Optional schema.org availability URL. Defaults to `InStock`. Pass
   * a different value (e.g. `https://schema.org/PreOrder`) when the
   * SKU's Stripe price ID is unset on this deploy — runtime checkout
   * degrades to a waitlist CTA in that case, and the structured data
   * must mirror that state. Misrepresenting an unavailable SKU as
   * `InStock` risks a Google "deceptive content" manual action.
   */
  availability?: string
  /**
   * Optional `sku` emitted on the Offer. Helps Google bind the rich
   * result to a stable inventory identifier across crawls and lets
   * AI-search engines cross-reference the same SKU mentioned by
   * partners / review sites. Use a stable slug (e.g. "ads-framework").
   */
  sku?: string
  /**
   * Optional `category` on the Offer (Google product category string,
   * e.g. "Software > Productivity"). Improves merchant-listing
   * categorisation in Google Shopping + AI agent commerce surfaces.
   */
  offerCategory?: string
  /**
   * Optional `sameAs` URLs identifying the SAME software entity on
   * external authority surfaces (e.g. the product's Product Hunt
   * launch page). AI-search citation graphs weight structured-review /
   * launch surfaces heavily when resolving product identity, so this
   * grounds the entity beyond first-party signals. Emitted verbatim
   * on the SoftwareApplication entity.
   */
  sameAs?: ReadonlyArray<string>
  /**
   * schema.org `applicationCategory` / `applicationSubCategory`. Default to
   * the ads-creative classification ("MultimediaApplication" / "Ad Creative
   * Authoring") for backward compatibility with the ads SKUs. Override for
   * non-ads SKUs — e.g. the Email Framework is email-marketing tooling, not
   * multimedia/ad-creative software — so search and agent-commerce surfaces
   * don't misclassify the SKU.
   */
  applicationCategory?: string
  applicationSubCategory?: string
}

/**
 * Build a schema.org SoftwareApplication entry. Paired with the
 * existing Product schema on a marketing page so search engines can
 * surface either snippet (Product card with price OR software card
 * with featureList + operating system).
 *
 * Publisher references the canonical Organization by `@id` (same
 * pattern as VideoObject) to avoid duplicating the global
 * Organization defined in `GlobalStructuredData`.
 *
 * When `reviews` is non-empty, the entity also ships an
 * `aggregateRating` (mean rating, count) plus a `review` array. Google
 * requires both fields to unlock the star-rating rich result for a
 * SoftwareApplication.
 */
/**
 * Build the canonical `hasMerchantReturnPolicy` block for an Offer.
 *
 * Google Search Console's "Top non-critical issues" surfaced a
 * `Missing field "hasMerchantReturnPolicy" (in "offers")` warning for
 * every Product/Offer that omitted this field — a Q1 2026 tightening
 * of the Merchant rich-result entitlement bar. Emit the block on
 * every Offer regardless of whether the page copy mentions a return
 * window: the structured data MUST exist for the eligibility check
 * even when the policy is "no returns" (use category
 * `MerchantReturnNotPermitted` in that case).
 *
 * For our digital SKUs:
 *   - Full frameworks → 30-day finite window before first zip download
 *   - Skills-only zip → discretionary 7-day, encoded as the same
 *     finite-window category with 7 days (visible FAQ copy controls
 *     the human-readable promise)
 *
 * `returnFees: FreeReturn` is honest for digital goods (no buyer
 * outlay to "return" a download — we just refund). `returnMethod:
 * ReturnByMail` is the Schema.org closest-match for "email support"
 * (no dedicated EmailReturn enum exists; ReturnByMail is the safe
 * default Google's parser accepts).
 */
export function buildMerchantReturnPolicy(
  policy: ReturnPolicyInput,
): Record<string, unknown> {
  const category = policy.category ?? "MerchantReturnFiniteReturnWindow"
  // `applicableCountry` is either a single string or an array — pass
  // through the array form unchanged so the consumer can express
  // multi-country coverage in one MerchantReturnPolicy block (Google's
  // parser accepts both shapes).
  // Copy the array form so callers' readonly arrays don't leak into the
  // emitted JSON-LD (where a downstream consumer might mutate).
  const applicableCountry: string | string[] =
    typeof policy.applicableCountry === 'string'
      ? policy.applicableCountry
      : [...policy.applicableCountry]
  const base: Record<string, unknown> = {
    "@type": "MerchantReturnPolicy",
    applicableCountry,
    returnPolicyCategory: `https://schema.org/${category}`,
    merchantReturnLink: policy.returnPolicyUrl ?? POLICY_URLS.returns,
  }
  // `merchantReturnDays` / `returnMethod` / `returnFees` are only
  // meaningful for finite + unlimited windows. Google's
  // MerchantReturnPolicy docs treat these fields as exclusive to those
  // categories — emitting them alongside `MerchantReturnNotPermitted`
  // produces structured data that's internally contradictory (the
  // category says "no returns" while the sibling fields describe one),
  // which keeps the Search Console "return-policy" warnings open
  // instead of clearing them. `r3315216958`.
  if (category !== "MerchantReturnNotPermitted") {
    base.merchantReturnDays = policy.days
    base.returnMethod = "https://schema.org/ReturnByMail"
    base.returnFees = "https://schema.org/FreeReturn"
  }
  return base
}

/**
 * Build the canonical `shippingDetails` block for a digital-download
 * Offer. Same Q1 2026 Google Search Console driver as
 * `hasMerchantReturnPolicy` above — the Merchant rich-result
 * entitlement requires the field even when the SKU "doesn't ship"
 * in the traditional sense.
 *
 * Pattern for downloadable software:
 *   - `shippingRate`: $0 (the download has no shipping cost)
 *   - `shippingDestination`: every country we serve (one
 *     DefinedRegion per country, ISO-3166-1 alpha-2)
 *   - `deliveryTime`: handlingTime/transitTime both 0–0 business
 *     days (downloads are immediate)
 *
 * Multiple `shippingDestination` entries are the canonical pattern
 * Google's structured-data team published for global digital SKUs;
 * a single DefinedRegion with multiple `addressCountry` entries
 * works but is harder for some downstream parsers (Bing) to consume.
 */
export function buildOfferShippingDetails(
  input: ShippingDetailsInput = {},
  priceCurrency: string,
): Record<string, unknown> {
  const countries =
    input.applicableCountries && input.applicableCountries.length > 0
      ? input.applicableCountries
      : DEFAULT_DIGITAL_OFFER_COUNTRIES
  const rate = input.shippingRate ?? { value: 0, currency: priceCurrency }
  return {
    "@type": "OfferShippingDetails",
    shippingRate: {
      "@type": "MonetaryAmount",
      value: rate.value,
      currency: rate.currency,
    },
    shippingDestination: countries.map((c) => ({
      "@type": "DefinedRegion",
      addressCountry: c,
    })),
    deliveryTime: {
      "@type": "ShippingDeliveryTime",
      // Schema.org requires QuantitativeValue for each sub-field.
      // `minValue: 0, maxValue: 0, unitCode: "DAY"` is the canonical
      // way to encode "immediate, same-day delivery" — what Google's
      // rich-result extractor matches against for "instant delivery"
      // badges on digital SKUs.
      handlingTime: {
        "@type": "QuantitativeValue",
        minValue: 0,
        maxValue: 0,
        unitCode: "DAY",
      },
      transitTime: {
        "@type": "QuantitativeValue",
        minValue: 0,
        maxValue: 0,
        unitCode: "DAY",
      },
    },
  }
}

/**
 * Default return-policy applied when an Offer doesn't specify one.
 * 30-day finite window matches the framework's published FAQ; the
 * skills-only SKU passes its own 7-day finite policy explicitly.
 * Either way the field is ALWAYS present on the Offer so the
 * Merchant rich-result check passes.
 *
 * `applicableCountry` mirrors `DEFAULT_DIGITAL_OFFER_COUNTRIES` so the
 * return-policy coverage matches the shipping coverage emitted by
 * `buildOfferShippingDetails` defaults — otherwise non-US shipping
 * destinations would have no matching return-policy entry and Search
 * Console's Merchant rich-result return-policy warnings for those
 * countries would persist instead of clearing. `r3315170447`.
 */
const DEFAULT_RETURN_POLICY: ReturnPolicyInput = {
  days: 30,
  applicableCountry: DEFAULT_DIGITAL_OFFER_COUNTRIES,
}

/**
 * Build the `aggregateRating` + `review` pair from a list of reviews,
 * for inclusion on any entity Google grades for star-rating rich
 * results (SoftwareApplication AND Product — the Product surface is the
 * one the Shopping-tab merchant listing reads, so the rating has to
 * live there too, not only on the SoftwareApplication twin).
 *
 * Returns an empty object when there are no reviews so callers can
 * spread it unconditionally (`{ ...entity, ...buildAggregateRatingAndReviews(r) }`).
 * `aggregateRating` is the mean rounded to one decimal — the form
 * Google's extractor renders. Reviews MUST be grounded in visible page
 * content to survive crawl-time verification.
 */
export function buildAggregateRatingAndReviews(
  reviews: ReadonlyArray<SchemaReview>,
): Record<string, unknown> {
  if (reviews.length === 0) return {}
  const sum = reviews.reduce((acc, r) => acc + r.ratingValue, 0)
  const mean = sum / reviews.length
  return {
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: Number(mean.toFixed(1)),
      reviewCount: reviews.length,
      bestRating: 5,
      worstRating: 1,
    },
    review: reviews.map((r) => ({
      "@type": "Review",
      reviewRating: {
        "@type": "Rating",
        ratingValue: r.ratingValue,
        bestRating: 5,
        worstRating: 1,
      },
      author: r.authorTitle
        ? { "@type": "Person", name: r.authorName, jobTitle: r.authorTitle }
        : { "@type": "Person", name: r.authorName },
      datePublished: r.datePublished,
      reviewBody: r.reviewBody,
    })),
  }
}

export function buildSoftwareApplicationJsonLd(
  input: SoftwareApplicationInput,
): Record<string, unknown> {
  const offer: Record<string, unknown> = {
    "@type": "Offer",
    price: input.price,
    priceCurrency: input.priceCurrency,
    availability: input.availability ?? "https://schema.org/InStock",
    url: input.url,
    // `itemCondition: NewCondition` is an explicit signal Google's
    // Merchant parser requires for software/physical-good ambiguous
    // SKUs to land in the new-products rich result (refurbished /
    // used software would use a different value). Cheap to emit.
    itemCondition: "https://schema.org/NewCondition",
    // ALWAYS emit shippingDetails + hasMerchantReturnPolicy — these
    // are the two fields Search Console flagged as "Missing" on every
    // Offer across the storefront. Defaults are digital-download
    // semantics (free, immediate, no shipping); callers can override
    // via the input's `shippingDetails` / `returnPolicy` keys.
    shippingDetails: buildOfferShippingDetails(
      input.shippingDetails,
      input.priceCurrency,
    ),
    hasMerchantReturnPolicy: buildMerchantReturnPolicy(
      input.returnPolicy ?? DEFAULT_RETURN_POLICY,
    ),
    ...(input.sku ? { sku: input.sku } : {}),
    ...(input.offerCategory ? { category: input.offerCategory } : {}),
  }

  if (input.buyActionTarget) {
    // BuyAction `potentialAction` on the Offer surfaces a
    // machine-readable checkout URL for AI agents (ChatGPT Atlas,
    // Claude agentic mode, Perplexity Buy). They follow this for
    // one-hop purchase flows rather than bouncing through the
    // marketing page; AgentReadyHQ measures a ~3.2× citation lift
    // vs Product+Offer alone in 2026 agentic-commerce reports.
    offer.potentialAction = {
      "@type": "BuyAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: input.buyActionTarget,
        actionPlatform: [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform",
        ],
      },
      result: {
        "@type": "Order",
        orderStatus: "https://schema.org/OrderProcessing",
      },
    }
  }

  const entity: Record<string, unknown> = {
    "@type": "SoftwareApplication",
    name: input.name,
    description: input.description,
    url: input.url,
    applicationCategory: input.applicationCategory ?? "MultimediaApplication",
    applicationSubCategory: input.applicationSubCategory ?? "Ad Creative Authoring",
    operatingSystem: "macOS, Linux, Windows",
    offers: offer,
    featureList: [...input.featureList],
    publisher: { "@id": ORGANIZATION_ID },
    ...(input.sameAs && input.sameAs.length > 0
      ? { sameAs: [...input.sameAs] }
      : {}),
    ...(input.softwareVersion ? { softwareVersion: input.softwareVersion } : {}),
    ...(input.downloadUrl ? { downloadUrl: input.downloadUrl } : {}),
    ...(input.fileFormat ? { fileFormat: input.fileFormat } : {}),
    ...(input.softwareRequirements
      ? { softwareRequirements: input.softwareRequirements }
      : {}),
  }

  if (input.reviews && input.reviews.length > 0) {
    // Shared with the Product entity (see `buildAggregateRatingAndReviews`)
    // so the SoftwareApplication twin and the merchant-listing Product
    // compute identical star-rating fields from one source.
    Object.assign(entity, buildAggregateRatingAndReviews(input.reviews))
  }

  return entity
}
